import Icon from '@/components/icons/Icon';
import { classnames, isSistaminuten } from '@/helpers';
import useMobileView from '@/hooks/useMobileView';
import { EmployeeStats, PlaceEmployee } from '@/types/state/shared';
import Avatar from '../Avatar';
import Label from '../Label/Label';
import Rating from '../redesign/Rating/Rating';
import Card from './Card';

import { NEW_PLACE_TWEAKS_EXPERIMENT } from '@/constants/experimentConstants';
import { useGetAmplitudeExperimentVariant } from '@/hooks/useAmplitudeExperiment';

type LegacyEmployeeListCardV1Props = {
  employee: PlaceEmployee;
  stats?: EmployeeStats[string];
  readMore?: React.ReactNode;
  rightSlot?: React.ReactNode;
  onClick?: () => void;
};

/**
 * Legacy employee card on the place page, rendered when reviews-v2 and listcards-v2 are both disabled.
 * Delete once both flags are fully rolled out.
 */
const LegacyEmployeeListCardV1 = ({ rightSlot, employee, onClick, stats }: LegacyEmployeeListCardV1Props) => {
  const getVariant = useGetAmplitudeExperimentVariant();
  const hasPlaceTweaksExperiment = getVariant(NEW_PLACE_TWEAKS_EXPERIMENT)?.value === 'on';

  const { isMobileView } = useMobileView();
  const { name, jobTitle, settings, info, certificates, avatar } = employee.about || {};
  const employeeJobTilte = (jobTitle || '').split('.').join('. ');
  const hasCertificate = certificates?.length || false;
  const acceptsGiftcard = !isSistaminuten() && settings?.acceptsGiftCard;
  const hasBadges = acceptsGiftcard || hasCertificate;

  return (
    <Card size="sm" childClassName="@container">
      <div data-cy="employee" className={classnames('gap-sm @sm:flex-row flex flex-col')}>
        <button
          onClick={onClick}
          disabled={!onClick}
          className="flex flex-1 items-center justify-between text-start outline-none">
          <div className="gap-sm flex flex-1 items-start overflow-hidden">
            <div className="gap-xs flex w-full flex-col">
              <div className="flex justify-between">
                <div className="gap-sm flex w-full">
                  <Avatar src={settings?.showPhoto === false ? null : avatar} size={'md'} />
                  <div className="gap-xs flex w-full flex-col justify-center overflow-hidden">
                    <div className="gap-sm flex w-full items-center justify-between">
                      <div className="gap-2xs flex flex-col">
                        <h2 className="text-l text-fg_primary">{name}</h2>
                        <div className="text-s gap-2xs flex items-start">
                          <div className={`text-fg_secondary flex w-full items-start`}>
                            {Boolean(employeeJobTilte) && employeeJobTilte}
                          </div>
                        </div>
                      </div>
                      {hasPlaceTweaksExperiment && (
                        <div className="text-s text-fg_link @sm:items-end flex flex-shrink-0">
                          <Icon variant="info-circle" style={{ width: '20px', height: '20px' }} color="fg_link" />
                        </div>
                      )}
                      {hasBadges && !hasPlaceTweaksExperiment && (
                        <div className="space-x-sm @sm:flex hidden flex-shrink-0 items-center">
                          {hasCertificate && (
                            <a
                              href="#certificates"
                              onClick={(e) => {
                                e.stopPropagation();
                              }}>
                              <Label variant="certificate" />
                            </a>
                          )}
                          {acceptsGiftcard && <Label variant="giftcard" />}
                        </div>
                      )}
                    </div>

                    {info && !hasPlaceTweaksExperiment && (
                      <span
                        className="text-s text-fg_secondary @sm:flex @sm:visible @sm:h-full invisible hidden h-0 items-start"
                        style={{
                          display: '-webkit-box',
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          WebkitLineClamp: 2,
                          textOverflow: 'ellipsis',
                        }}>
                        {info}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              {info && !hasPlaceTweaksExperiment && (
                <span
                  className="text-s text-fg_secondary @sm:hidden @sm:invisible @sm:h-0  not-sr-only visible flex items-start"
                  style={{
                    display: '-webkit-box',
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    WebkitLineClamp: 2,
                    textOverflow: 'ellipsis',
                  }}>
                  {info}
                </span>
              )}
              {hasBadges && !hasPlaceTweaksExperiment && (
                <div className="space-x-sm @sm:hidden flex items-center">
                  {hasCertificate && (
                    <a
                      href="#certificates"
                      onClick={(e) => {
                        e.stopPropagation();
                      }}>
                      <Label variant="certificate" />
                    </a>
                  )}

                  {acceptsGiftcard && <Label variant="giftcard" />}
                </div>
              )}
            </div>
          </div>
        </button>
        <div className={`border-border_regular @sm:border-l @sm:border-t-0 border-t`}>
          <div
            className={classnames(
              'text-s gap-sm flex h-full items-center',
              'pt-sm @sm:pt-0 @sm:pl-sm @sm:py-xs',
              Boolean(stats?.count) ? 'justify-between' : 'justify-end',
              hasPlaceTweaksExperiment && 'min-w-[170px] justify-end',
            )}>
            {Boolean(stats?.count) && (
              <div className="gap-xs @sm:gap-2xs @sm:flex-col @sm:items-end flex items-center">
                <Rating ratingCount={stats.count} avgRating={stats?.score ?? 0} size={'md'} showOneStar={true} />
                <span
                  className={`flex ${
                    isMobileView
                      ? 'before:w-xxs before:h-xxs before:mr-xs before:bg-fg_secondary before:mt-[5px] before:inline-block before:rounded-full before:content-[""]'
                      : ''
                  } text-fg_secondary`}>
                  {stats?.count} betyg
                </span>
              </div>
            )}
            {rightSlot && rightSlot}
          </div>
        </div>
      </div>
    </Card>
  );
};

export default LegacyEmployeeListCardV1;
